import { useTranslation } from "react-i18next";

import { faArrowLeft, faArrowRight } from "@fortawesome/pro-regular-svg-icons";
import { useEffect, useState } from "react";

import { Icon } from "@/components";
import FormContainer from "@/pages/Public/components/FormContainer";

import NeedsAnalysisStepIndicator from "./NeedsAnalysisStepIndicator";
import NeedsAnalysisStepBody from "./NeedsAnalysisStepBody";
import { QuestionnaireStep, QuestionnaireStepOption } from "../../schema/questionnaire";
import QuestionnaireOption from "../QuestionnaireStep/QuestionnaireOption";

interface NeedsAnalysisStepsProps {
	stepInfo?: QuestionnaireStep;
	activeStep: number;
	totalStepLength: number;
	currentAnswer?: QuestionnaireStepOption | null;
	handleNext: (currentAnswer: QuestionnaireStepOption) => void;
	handleBack: (currentAnswer: QuestionnaireStepOption | null) => void;
	isLoading: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	formError?: any;
}

export default function NeedsAnalysisSteps({
	activeStep,
	handleBack,
	handleNext,
	isLoading,
	totalStepLength,
	stepInfo,
	currentAnswer,
	formError,
}: NeedsAnalysisStepsProps) {
	const { t } = useTranslation();
	const ts = (key: string, options = {}) =>
		t(`questionnaire.needs-analysis.progress.${key}`, options);
	const [answer, setAnswer] = useState<QuestionnaireStepOption | null>(currentAnswer ?? null);

	useEffect(() => {
		if (currentAnswer && (!answer || currentAnswer?.id !== answer.id)) {
			setAnswer(currentAnswer);
		}
	}, [currentAnswer]);

	return (
		<div className="h-fit mt-24 mb-24 sspace-y-4">
			<NeedsAnalysisStepIndicator currentStepIndex={activeStep - 1} totalSteps={totalStepLength} />
			<FormContainer
				changeActionsDirection
				actionContainerClassName="flex gap-2 !space-y-0"
				disableSecondaryButton={activeStep === 1}
				formContainerClassName="h-fit"
				formError={formError}
				formHeader={
					<p className="text-[14px] font-medium text-primary-gray-lighter mb-6">
						{ts("step", {
							currentStep: activeStep,
							totalLength: totalStepLength,
						})}
					</p>
				}
				handleSubmit={() => {
					if (answer) {
						handleNext(answer);

						if (stepInfo?.stepNumber && stepInfo?.stepNumber !== totalStepLength) {
							setAnswer(null);
						}
					}
				}}
				isLoading={isLoading}
				secondaryButtonAction={() => {
					handleBack(answer);
					if (stepInfo?.stepNumber && stepInfo?.stepNumber > 1) {
						setAnswer(null);
					}
				}}
				secondaryButtonText={
					<p className="text-[14px] text-primary-blue-dark font-medium flex items-center justify-center gap-2">
						<Icon className="w-4 h-3" icon={faArrowLeft} />
						{ts("back")}
					</p>
				}
				secondaryButtonType="button"
				showLogo={false}
				submitButtonText={
					<p className="text-[14px] text-white font-medium flex items-center justify-center gap-2">
						{ts(stepInfo?.stepNumber === totalStepLength ? "finish" : "next")}
						<Icon className="w-4 h-3 text-white" icon={faArrowRight} />
					</p>
				}
				submitButtonType="button"
				title={stepInfo?.title ?? ""}
			>
				<NeedsAnalysisStepBody description={stepInfo?.description ?? ""} />
				<div className="space-y-2 mt-6">
					{stepInfo?.options
						?.slice()
						?.sort((a, b) => (a?.id && b?.id ? a.id - b.id : 0))
						?.map((option, index) => {
							return (
								<QuestionnaireOption
									key={index}
									handleSelect={() => setAnswer(option)}
									option={option?.title ?? ""}
									selected={answer?.id === option?.id}
								/>
							);
						})}
				</div>
			</FormContainer>
		</div>
	);
}
